import { Injectable, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import * as COPSelectors from '@app/modules/cop/store/cop/cop.selectors';
import * as COPActions from '@app/modules/cop/store/cop/cop.actions';
import { MapWallDisplayActions } from './map-wall-display.actions';
import { MapWallDisplayFeature } from './map-wall-display.feature';
import { WallDisplayConfiguration } from '../models';
import { DatasetType } from '@app/modules/core/store/models/dataset.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MapWallDisplayFacade {
  private store = inject(Store);

  readonly isActive = this.store.selectSignal(MapWallDisplayFeature.selectIsActive);
  readonly configuration = this.store.selectSignal(MapWallDisplayFeature.selectConfiguration);
  readonly showTimesWidget = computed(() => this.configuration()?.showTimes);
  readonly showLocationsWidget = computed(() => this.configuration()?.showLocations);
  readonly showWorkflowStatusWidget = computed(() => this.configuration()?.showStatus);
  readonly showMapLegendWidget = computed(() => this.configuration()?.showMapLegend);
  readonly showAlertFeedWidget = computed(() => this.configuration()?.showAlertFeed);
  readonly showImageCardWidget = computed(() => this.configuration()?.showImageCard);
  readonly imageCardSettings = computed(() => this.configuration()?.imageCardSettings);
  readonly locationCardSettings = computed(() => this.configuration()?.locationCardSettings);
  readonly locations = computed(() => this.configuration()?.locations ?? []);
  readonly timeCardSettings = computed(() => this.configuration()?.timeCardSettings);
  readonly timesLocations = computed(() => this.configuration()?.times ?? []);
  readonly widgetConfigFormType$ = this.store.select(MapWallDisplayFeature.selectWidgetFormType);

  // ---- from other features ----
  readonly isMapInWorkbenchMode = this.store.selectSignal(COPSelectors.selectIsMapEdit);
  readonly isMapLoading = this.store.selectSignal(COPSelectors.selectIsMapLoading);
  readonly isMapWallEdit = this.store.selectSignal(COPSelectors.selectIsMapWallEdit);
  readonly workflowStatusSummaries = this.store.selectSignal(COPSelectors.selectWorkflowStatusSummary);
  readonly mapPosition = this.store.selectSignal(COPSelectors.selectMapPosition);
  readonly mapState = this.store.selectSignal(COPSelectors.selectSelectedMapState);
  readonly mapStateWithDebounce = toSignal(this.store.select(COPSelectors.selectSelectedMapState).pipe(debounceTime(3000)));
  readonly mapSettings = this.store.selectSignal(COPSelectors.selectMapSettings);
  readonly activeIncidentCount = computed(() => {
    if (!this.showWorkflowStatusWidget()) {
      return;
    }

    const { datasetsSet = {}, layersSet = {}, layerStates = {} } = this.mapState() ?? {};

    if (!Boolean(datasetsSet) || !Boolean(layersSet)) {
      return 0;
    }

    const layers = Object.values(layersSet);

    if (!layers.length) {
      return 0;
    }

    const incidentDatasetUIdMap = {};

    const stepUIdsToFilter = Object.values(datasetsSet).reduce(
      (acc, { type, workflow }) => {
        if (type === DatasetType.Incident && !!workflow) {
          const { workflowSteps = [], uId } = workflow;

          incidentDatasetUIdMap[uId] = true;

          if (workflowSteps.length) {
            const stepToFilter =
              workflowSteps.find(wfs => wfs.configuration?.completeStep) ??
              [...workflowSteps].sort((a, b) => a.sortOrder - b.sortOrder).at(-1);
            acc[stepToFilter.uId] = true;
          }
        }

        return acc;
      },
      {} as Record<string, boolean>
    );

    const count = layers.reduce((acc, { datasetUId, uId }) => {
      if (incidentDatasetUIdMap[datasetUId]) {
        const docCount = Object.values(layerStates[uId]?.documents ?? {}).filter(d => !stepUIdsToFilter[d?.workflow_step_uid]).length ?? 0;
        return acc + docCount;
      }

      return acc;
    }, 0);

    return count;
  });

  readonly isMapLayersLoading = computed(() => {
    const { layerStates = {} } = this.mapState() ?? {};
    return Object.values(layerStates).some(({ isLoading = false }) => isLoading);
  });

  updateConfiguration(configuration: WallDisplayConfiguration): void {
    this.store.dispatch(MapWallDisplayActions.updateConfiguration({ configuration }));
    this.store.dispatch(COPActions.setMapDirtyToStore());
  }

  hideWidgetConfigForm(): void {
    this.store.dispatch(MapWallDisplayActions.hideWidgetConfig());
  }

  showLocationsWidgetConfigForm(): void {
    this.store.dispatch(MapWallDisplayActions.showLocationsWidgetConfig());
  }

  showTimesWidgetConfigForm(): void {
    this.store.dispatch(MapWallDisplayActions.showTimesWidgetConfig());
  }

  showImageWidgetConfigForm(): void {
    this.store.dispatch(MapWallDisplayActions.showImageWidgetConfig());
  }

  showAlertsFeedWidgetConfigForm(): void {
    this.store.dispatch(MapWallDisplayActions.showAlertsFeedWidgetConfig());
  }

  refreshMap(): void {
    this.store.dispatch(COPActions.refreshSelectedMap());
  }
}
